import {
  REMOTE_CONFIG_JSON_EMPTY_MESSAGE,
  REMOTE_CONFIG_JSON_INVALID_MESSAGE,
  REMOTE_CONFIG_JSON_SINGLE_VALUE_MESSAGE,
  REMOTE_CONFIG_NUMBER_MESSAGE,
  RemoteConfigApp,
  RemoteConfigValueType,
} from 'src/constants/remote-config/remoteConfig'
import type { RemoteConfig, RemoteConfigFormValues } from 'src/interfaces/remoteConfig'

const toValueText = (type: RemoteConfigValueType, value: unknown): string => {
  if (type === RemoteConfigValueType.Json) {
    return JSON.stringify(value, null, 2)
  }

  if (type === RemoteConfigValueType.String && typeof value === 'string') {
    return value
  }

  if (type === RemoteConfigValueType.Number && typeof value === 'number') {
    return String(value)
  }

  return ''
}

export const buildRemoteConfigFormValues = (
  remoteConfig: RemoteConfig | null,
  defaultApp: RemoteConfigApp,
): RemoteConfigFormValues => {
  if (!remoteConfig) {
    return {
      key: '',
      app: defaultApp,
      type: RemoteConfigValueType.Json,
      description: '',
      valueText: '',
      valueBoolean: true,
      enabled: true,
    }
  }

  return {
    key: remoteConfig.key,
    app: remoteConfig.app,
    type: remoteConfig.type,
    description: remoteConfig.description,
    valueText: toValueText(remoteConfig.type, remoteConfig.value),
    // The switch only matters for a boolean; other types keep the new-key default (on).
    valueBoolean:
      remoteConfig.type === RemoteConfigValueType.Boolean ? remoteConfig.value === true : true,
    enabled: remoteConfig.enabled,
  }
}

/** The API value for the form. Call it only on values the validator has passed. */
export const toRemoteConfigValue = (values: RemoteConfigFormValues): unknown => {
  if (values.type === RemoteConfigValueType.Json) {
    return JSON.parse(values.valueText)
  }

  if (values.type === RemoteConfigValueType.Number) {
    return Number(values.valueText.trim())
  }

  if (values.type === RemoteConfigValueType.Boolean) {
    return values.valueBoolean
  }

  return values.valueText
}

/** The ticket's message for a value the API would refuse, or null. Booleans and text are always valid. */
export const validateRemoteConfigValueText = (
  type: RemoteConfigValueType,
  valueText: string,
): string | null => {
  if (type === RemoteConfigValueType.Json) {
    if (valueText.trim() === '') {
      return REMOTE_CONFIG_JSON_EMPTY_MESSAGE
    }

    let parsed: unknown

    try {
      parsed = JSON.parse(valueText)
    } catch {
      return REMOTE_CONFIG_JSON_INVALID_MESSAGE
    }

    if (parsed === null || typeof parsed !== 'object') {
      return REMOTE_CONFIG_JSON_SINGLE_VALUE_MESSAGE
    }

    return null
  }

  if (type === RemoteConfigValueType.Number) {
    const trimmed = valueText.trim()

    if (trimmed === '' || !Number.isFinite(Number(trimmed))) {
      return REMOTE_CONFIG_NUMBER_MESSAGE
    }
  }

  return null
}

/** The size the API checks: UTF-8 bytes of the value's JSON. */
export const getRemoteConfigValueBytes = (value: unknown): number => {
  return new TextEncoder().encode(JSON.stringify(value)).length
}

export const formatJsonText = (text: string): string | null => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2)
  } catch {
    return null
  }
}
